import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { CookieService } from 'ngx-cookie-service';
import { Socket } from 'ngx-socket-io';
import { Observable } from 'rxjs';
import { environment } from '../../environments/environment';
import { IProvider } from '../iprovider';
import { map } from 'rxjs/operators';

@Injectable()
export class BaseService implements IProvider {
  private _lang: string;
  public get lang(): string {
    this._lang = this.cookieService.get('lang') || 'fa';
    return this._lang;
  }
  public set lang(v: string) {
    if (v !== this._lang) {
      this._lang = v;
      this.cookieService.set('lang', this._lang, undefined, undefined, undefined, false, 'Strict');
    }
  }

  private _token: string;
  public get token(): string {
    this._token = this.cookieService.get('token');
    return this._token;
  }
  public set token(v: string) {
    if (v !== this._token) {
      this._token = v;
      this.cookieService.set('token', this._token, undefined, undefined, undefined, false, 'Strict');
    }
  }

  private _username: string;
  public get username(): string {
    this._username = this.cookieService.get('username');
    return this._username;
  }
  public set username(v: string) {
    if (v !== this._username) {
      this._username = v;
      this.cookieService.set('username', this._username, undefined, undefined, undefined, false, 'Strict');
    }
  }

  private _selectedAccount: string;
  public get selectedAccount(): string {
    this._selectedAccount = sessionStorage.getItem('selectedAccount');
    return this._selectedAccount;
  }
  public set selectedAccount(v: string) {
    if (v !== this._selectedAccount) {
      this._selectedAccount = v;
      sessionStorage.setItem('selectedAccount', this._selectedAccount);
    }
  }

  private _latitude: number;
  public get latitude(): number {
    if (!this._latitude) {
      this._latitude = +this.cookieService.get('latitude');
    }
    return this._latitude;
  }
  public set latitude(v: number) {
    if (v !== this._latitude) {
      this._latitude = v;
      this.cookieService.set('latitude', this._latitude.toString(), undefined, undefined, undefined, false, 'Strict');
    }
  }


  private _longitude: number;
  public get longitude(): number {
    if (!this._longitude) {
      this._longitude = +this.cookieService.get('longitude');
    }
    return this._longitude;
  }
  public set longitude(v: number) {
    if (v !== this._longitude) {
      this._longitude = v;
      this.cookieService.set('longitude', this._longitude.toString(), undefined, undefined, undefined, false, 'Strict');
    }
  }


  url = environment.url;

  // The 'connect' handler below belongs to the socket, not to a service instance:
  // every service that extends BaseService is a separate injectable sharing the
  // one socket, so without this guard each of them would add its own listener.
  private static authBound = false;

  constructor(
    public http: HttpClient,
    public cookieService: CookieService,
    public socket: Socket) {
    this.bindReauth();
  }

  /**
   * Re-sends `auth` on every (re)connect.
   *
   * socket.io reconnects transparently after a network blip, but the API sees a
   * brand new socket with empty `socket.data` and no room membership: it has no
   * idea who is on the other end until `auth` arrives again. Everything the API
   * gates on the verified token -- targeted messages, and the net:* diagnostics
   * commands -- would silently stay broken until the page was reloaded.
   *
   * The initial `auth` is still sent by the login and layout components; this
   * only restores it afterwards, and does nothing while nobody is logged in.
   */
  private bindReauth() {
    if (BaseService.authBound) { return; }
    BaseService.authBound = true;
    this.socket.on('connect', () => {
      if (this.cookieService.get('token')) {
        this.authenticate().catch(error => console.error('re-auth failed', error));
      }
    });
  }

  /**
   * Sends `auth` and resolves once the API has verified the token, so a caller
   * can wait for the socket to be identified instead of racing it.
   *
   * The acknowledgement is a recent addition on the API side; against a server
   * that does not send one this resolves on a timeout rather than rejecting, so
   * the caller degrades to the old fire-and-forget behaviour instead of failing.
   */
  authenticate(token: string = this.cookieService.get('token')): Promise<any> {
    return new Promise((resolve, reject) => {
      if (!token) {
        reject(new Error('NO_TOKEN'));
        return;
      }
      let settled = false;
      const timer = setTimeout(() => {
        if (settled) { return; }
        settled = true;
        resolve(null);
      }, 5000);

      this.socket.emit('auth', token, (ack: any) => {
        if (settled) { return; }
        settled = true;
        clearTimeout(timer);
        if (ack && ack.error) {
          reject(new Error(ack.error));
        } else {
          resolve(ack ? ack.data : null);
        }
      });
    });
  }

  /** The API's version (its package.json, YY.MM.DD.HHmm) as plain text. */
  public getVersion() {
    return this.http.get(`${this.url}api/version`, { responseType: 'text' });
  }

  public checkAccess(action, entity, ref, field) {
    const headers = new HttpHeaders({
      'Content-Type': 'application/json',
      'token': this.token,
      'latitude': this.latitude.toString(),
      'longitude': this.longitude.toString()
    });

    const body = {
      action,
      entity,
      ref,
      field
    };
    return this.http.post(this.url + 'api/security/checkAccess', body, { headers: headers });
  }
  apiCall(api: string, body: any, timestamp = (+new Date()).toString(), options?: any): Observable<any> {
    const headers = new HttpHeaders({
      'Content-Type': 'application/json',
      'token': this.token,
      'lang': this.lang,
      'latitude': this.latitude.toString(),
      'longitude': this.longitude.toString(),
      'timestamp': timestamp
    });

    return this.http.post(this.url + api, body || {}, { headers: headers, responseType: options?.responseType });
  }
  getReadableColumns(entity): Observable<any> {
    const headers = new HttpHeaders({
      'Content-Type': 'application/json',
      'token': this.token
    });
    const body = { entity };

    return this.http.post(this.url + 'api/security/getReadableColumns', body, { headers: headers });
  }

  perform(action: string, row: any, options?: any): Observable<any> {
    return;
  }

  menu(selectedAccount?) {
    const headers = new HttpHeaders({
      'Content-Type': 'application/json',
      'token': this.token,
      'lang': this.lang,
      'selected-account': selectedAccount || ''
    });

    return this.http.get(`${this.url}api/menu`, { headers: headers });
  }

  form(entity, sub, mode) {

    const headers = new HttpHeaders({
      'Content-Type': 'application/json',
      'token': this.token,
      'lang': this.lang,
      'mode': mode || ''
    });

    return this.http.get(`${this.url}api/form/${entity}${sub ? '.' + sub : ''}`, { headers: headers });

  }

  list(entity, sub) {
    const headers = new HttpHeaders({
      'Content-Type': 'application/json',
      'token': this.token,
      'lang': this.lang
    });

    return this.http.get(`${this.url}api/list/${entity}${sub ? '.' + sub : ''}`, { headers: headers });
  }

  call() {
    const headers = new HttpHeaders({
      'Content-Type': 'application/json',
      'token': this.token
    });

    return this.http.get(`${this.url}api/call`, { headers: headers });
  }

  sendMessage(msg: string) {
    this.socket.emit('message', msg);
  }

  getMessage() {
    return this.socket.fromEvent('message').pipe(map((data: any) => data.msg));
  }
}
